import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { FaPlusCircle } from 'react-icons/fa'
import { FaX } from 'react-icons/fa6'
import Select from 'react-select'
import { z } from 'zod'

import { Button } from '../../../../components/Button'
import { Input } from '../../../../components/Input'
import { SwitchInput } from '../../../../components/SwitchInput'
import { useAppSelector } from '../../../../hooks/useAppSelector'
import { useDialogBox } from '../../../../hooks/useDialogBox'
import { usePermission } from '../../../../hooks/usePermission'
import { Role } from '../../../../interfaces/rolesEnum'
import { SelectOption } from '../../../../reducers/appReducer'
import { setCityByStateOptionsAction } from '../../../../services/appServices'
import { getAllPromotersCompanies } from '../../../../services/promotersCompaniesServices'
import { createStore, getAllStoreGroups, Store, updateStore } from '../../../../services/storesServices'
import { getUsersByRole } from '../../../../services/userServices'

interface ModalUpsertStoreProps {
  onClose: () => void
  refetchList: () => void
  onClickCreateGroupStore: () => void
  store?: Store | null
}

const upsertStoreFormSchema = z.object({
  name: z.string(),
  state: z.string(),
  city: z.string(),
  store_group_id: z.string(),
  seller_id: z.string().optional().nullable(),
  supervisor_id: z.string().optional(),
  trade_marketing_coordinator_id: z.string().optional(),
  promoters: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
    }),
  ).optional(),
  sellers: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
    }),
  ).optional(),
  promoters_company_id: z.string().optional().nullable(),
})

type UpsertStoreFormData = z.infer<typeof upsertStoreFormSchema>

export function ModalUpsertStore({ onClose, refetchList, store, onClickCreateGroupStore }: ModalUpsertStoreProps) {
  const [isLoadingForm, setIsLoadingForm] = useState(false)

  const [hasMultipleSellers, setHasMultipleSellers] = useState(false)

  const [sellerSelect, setSellerSelect] = useState<SelectOption[]>([])
  const [supervisorsSelect, setSupervisorsSelect] = useState<SelectOption[]>([])
  const [tradeMarketingCoordinatorsSelect, setTradeMarketingCoordinatorsSelect] = useState<SelectOption[]>([])
  const [storeGroupSelect, setStoreGroupSelect] = useState<SelectOption[]>([])

  const { defaultFormData } = useAppSelector(state => state.app)
  const { showDialog } = useDialogBox()

  const {
    createStore: canCreate,
    updateStore: canUpdate,
    updateStorePromoters,
    updateStoreSupervisor,
    updateStoreTradeMarketingCoordinator,
    upsertStoreGroup,
    editStorePromotersCompany,
  } = usePermission()

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: {
      errors,
    },
  } = useForm<UpsertStoreFormData>({
    resolver: zodResolver(upsertStoreFormSchema),
    defaultValues: {
      name: store?.name,
      state: store?.state,
      city: store?.city,
      store_group_id: store?.store_group_id,
      seller_id: store?.seller_id || undefined,
      supervisor_id: store?.supervisor_id || undefined,
      trade_marketing_coordinator_id: store?.trade_marketing_coordinator_id || undefined,
      promoters: store?.store_promoter
        ? store?.store_promoter.map(({ promoter }) => ({ id: promoter.id, name: promoter.name }))
        : [],
      sellers: store?.sellers_list
        ? store?.sellers_list.map(({ seller }) => ({ id: seller.id, name: seller.name }))
        : [],
      promoters_company_id: store?.promoters_company_id,
    },
  })

  const selectedState = watch('state')

  // const selectedSeller = watch('seller_id')
  const selectedMultipleSellers = watch('sellers')

  const selectedPromoters = watch('promoters')
  const selectedPromotersCompany = watch('promoters_company_id')

  async function handleSubmitUpsertStoreForm(data: UpsertStoreFormData) {
    setIsLoadingForm(true)
    let result
    if (store) {
      result = await updateStore({
        storeId: store.id,
        name: data.name,
        state: data.state,
        city: data.city,
        seller_id: !hasMultipleSellers && data.sellers && data.sellers.length === 0
          ? data.seller_id
          : null,
        supervisor_id: data.supervisor_id,
        trade_marketing_coordinator_id: data.trade_marketing_coordinator_id,
        store_group_id: data.store_group_id,
        promoter_ids: data.promoters
          ? data.promoters.map(promoter => promoter.id)
          : undefined,
        seller_ids: hasMultipleSellers && data.sellers
          ? data.sellers.map(seller => seller.id)
          : undefined,
        promoters_company_id: data.promoters_company_id,
      })
    } else {
      result = await createStore({
        name: data.name,
        state: data.state,
        city: data.city,
        store_group_id: data.store_group_id,
        seller_id: !hasMultipleSellers && data.sellers && data.sellers.length === 0
          ? data.seller_id
          : null,
        supervisor_id: data.supervisor_id,
        trade_marketing_coordinator_id: data.trade_marketing_coordinator_id,
        promoter_ids: data.promoters
          ? data.promoters.map(promoter => promoter.id)
          : undefined,
        seller_ids: hasMultipleSellers && data.sellers
          ? data.sellers.map(seller => seller.id)
          : undefined,
        promoters_company_id: data.promoters_company_id,
      })
    }

    if (result) {
      if (result.success) {
        showDialog({
          title: 'Sucesso',
          description: result.message,
          type: 'success',
        })
        refetchList()
        onClose()
      } else {
        showDialog({
          title: 'Erro',
          description: result.message,
        })
      }
    }
    setIsLoadingForm(false)
  }

  function handleAddSeller(data: { id: string, name: string }) {
    if (selectedMultipleSellers) {
      if (selectedMultipleSellers.some(sell => sell.id === data.id)) return
      setValue('sellers', [data, ...selectedMultipleSellers])
    }
  }

  function handleRemoveSeller(id: string) {
    if (selectedMultipleSellers) {
      const filteredList = selectedMultipleSellers.filter(sell => sell.id !== id)
      setValue('sellers', filteredList)
    }
  }

  function handleAddPromoter(data: { id: string, name: string }) {
    if (selectedPromoters) {
      if (selectedPromoters.some(prom => prom.id === data.id)) return
      setValue('promoters', [data, ...selectedPromoters])
    }
  }

  function handleRemovePromoter(id: string) {
    if (selectedPromoters) {
      const filteredList = selectedPromoters.filter(prom => prom.id !== id)
      setValue('promoters', filteredList)
    }
  }

  function handleClickAddStoreGroup() {
    onClickCreateGroupStore()
    onClose()
  }

  function toggleHasMultipleSellers(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.checked) {
      setValue('seller_id', null)
    } else {
      setValue('sellers', [])
    }
    setHasMultipleSellers(e.target.checked)
  }

  const { data: promotersCompanySelect } = useQuery({
    queryKey: ['stores_promoters_company_select'],
    queryFn: async () => {
      const { promotersCompanies } = await getAllPromotersCompanies()
      return promotersCompanies.map(item => ({ value: item.id, label: item.name }))
    },
  })

  const { data: promoterSelect } = useQuery({
    queryKey: ['stores_promoters_select', selectedPromotersCompany],
    queryFn: async () => {
      const { users: promoters } = await getUsersByRole(Role.PROMOTER)
      if (selectedPromotersCompany) {
        return promoters
          .filter(item => item.promoter_of_company_id === selectedPromotersCompany)
          .map(promoter => ({ label: promoter.name, value: promoter.id }))
      }
      return promoters.map(promoter => ({ label: promoter.name, value: promoter.id }))
    },
  })

  const defineSelects = useCallback(async () => {
    const [{ users: sellers }, { users: supervisors }, { users: tradeMarketingCoordinators }, { storeGroups }] = await Promise.all([
      getUsersByRole(Role.SELLER),
      getUsersByRole(Role.SUPERVISOR),
      getUsersByRole(Role.TRADE_MARKETING_COORDINATOR),
      getAllStoreGroups(),
    ])
    setSellerSelect(sellers.map(seller => ({ label: seller.name, value: seller.id })))
    setSupervisorsSelect(supervisors.map(supervisor => ({ label: supervisor.name, value: supervisor.id })))
    setTradeMarketingCoordinatorsSelect(
      tradeMarketingCoordinators
        .map(tradeMarketingCoordinator => ({
          label: tradeMarketingCoordinator.name,
          value: tradeMarketingCoordinator.id,
        })),
    )
    setStoreGroupSelect(storeGroups.map(storeGroups => ({ label: storeGroups.name, value: storeGroups.id })))
  }, [])

  useEffect(() => {
    setIsLoadingForm(true)
    defineSelects().finally(() => setIsLoadingForm(false))
  }, [defineSelects])

  useEffect(() => {
    setCityByStateOptionsAction(selectedState)
  }, [selectedState])

  useEffect(() => {
    if (!store) return
    setHasMultipleSellers(store.sellers_list.length > 0)
  }, [store])

  return (
    <div
      className="fixed top-0 left-0 w-screen h-screen bg-black/65 flex items-center justify-center z-[10000]"
      onClick={onClose}
    >
      <div
        className="flex flex-col bg-slate-100 relative rounded-md p-4 w-[80vw] max-h-[80dvh] md:w-96"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex justify-between mb-4">
          <h1 className="font-semibold text-2xl">
            {store
              ? 'Editar'
              : 'Criar'} loja
          </h1>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <FaX />
          </Button>
        </header>
        <form
          className="flex flex-col space-y-4 w-full overflow-y-auto"
          onSubmit={handleSubmit(handleSubmitUpsertStoreForm)}
        >
          {store &&
            <div>
              <Input
                placeholder="Identificador"
                label="ID"
                value={store?.id}
                readOnly
                disabled
              />
            </div>}
          <div className="flex items-end">
            <div className="flex-1">
              <Controller
                name="store_group_id"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <>
                    <label
                      className="block text-sm/6 font-medium text-gray-900"
                      htmlFor="store_group_id"
                    >
                      Grupo
                    </label>
                    <Select
                      id="store_group_id"
                      options={storeGroupSelect}
                      onChange={state => onChange(state?.value)}
                      value={storeGroupSelect.filter(city => city.value === value)}
                      placeholder="Grupo..."
                      isDisabled={!canCreate || !canUpdate}
                    />
                    {
                      errors.state && (
                        <span className="text-xs font-medium text-red-500">
                          {errors.state.message}
                        </span>
                      )
                    }
                  </>
                )}
              />
            </div>
            {upsertStoreGroup && (
              <Button
                variant="outline"
                title="Criar novo grupo"
                type="button"
                className="ml-2 w-12 h-9"
                onClick={handleClickAddStoreGroup}
              >
                <FaPlusCircle />
              </Button>
            )}
          </div>
          <div>
            <Input
              placeholder="Nome"
              label="Nome"
              disabled={!canCreate || !canUpdate}
              required
              {...register('name')}
            />
          </div>
          <div>
            <Controller
              name="state"
              control={control}
              render={({ field: { onChange, value } }) => (
                <>
                  <label
                    className="block text-sm/6 font-medium text-gray-900"
                    htmlFor="state"
                  >
                    Estado (UF)
                    <span className="text-red-500 ml-2">
                      *
                    </span>
                  </label>
                  <Select
                    id="state"
                    required
                    options={defaultFormData.stateSelect}
                    onChange={state => onChange(state?.value)}
                    value={defaultFormData.stateSelect.filter(city => city.value === value)}
                    defaultInputValue={value}
                    isDisabled={!canCreate || !canUpdate}
                    placeholder="Estado..."
                  />
                  {
                    errors.state && (
                      <span className="text-xs font-medium text-red-500">
                        {errors.state.message}
                      </span>
                    )
                  }
                </>
              )}
            />
          </div>
          <div>
            <Controller
              name="city"
              control={control}
              render={({ field: { onChange, value } }) => (
                <>
                  <label
                    className="block text-sm/6 font-medium text-gray-900"
                    htmlFor="city"
                  >
                    Cidade
                    <span className="text-red-500 ml-2">
                      *
                    </span>
                  </label>
                  <Select
                    id="city"
                    options={defaultFormData.citySelect}
                    onChange={state => onChange(state?.value)}
                    value={defaultFormData.citySelect.filter(city => city.value === value)}
                    placeholder={!selectedState
                      ? 'Selecione um estado'
                      : 'Cidade...'}
                    isDisabled={!selectedState || !canCreate || !canUpdate}
                  />
                  {
                    errors.city && (
                      <span className="text-xs font-medium text-red-500">
                        {errors.city.message}
                      </span>
                    )
                  }
                </>
              )}
            />
          </div>
          {updateStoreSupervisor && (
            <div>
              <Controller
                name="supervisor_id"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <>
                    <label
                      className="block text-sm/6 font-medium text-gray-900"
                      htmlFor="supervisor_id"
                    >
                      Supervisor
                    </label>
                    <Select
                      id="supervisor_id"
                      options={supervisorsSelect}
                      onChange={state => onChange(state?.value)}
                      value={supervisorsSelect.filter(supervisorsSelect => supervisorsSelect.value === value)}
                      placeholder="Supervisor..."
                      isDisabled={!canCreate || !canUpdate}
                    />
                    {
                      errors.state && (
                        <span className="text-xs font-medium text-red-500">
                          {errors.state.message}
                        </span>
                      )
                    }
                  </>
                )}
              />
            </div>
          )}
          {updateStoreTradeMarketingCoordinator && (
            <div>
              <Controller
                name="trade_marketing_coordinator_id"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <>
                    <label
                      className="block text-sm/6 font-medium text-gray-900"
                      htmlFor="trade_marketing_coordinator_id"
                    >
                      Coordenador de Trade Marketing
                    </label>
                    <Select
                      id="trade_marketing_coordinator_id"
                      options={tradeMarketingCoordinatorsSelect}
                      onChange={state => onChange(state?.value)}
                      value={tradeMarketingCoordinatorsSelect.filter(tradeMarketingCoordinator => tradeMarketingCoordinator.value === value)}
                      placeholder="coordenador de trade marketing..."
                      isDisabled={!canCreate || !canUpdate}
                    />
                    {
                      errors.state && (
                        <span className="text-xs font-medium text-red-500">
                          {errors.state.message}
                        </span>
                      )
                    }
                  </>
                )}
              />
            </div>
          )}
          <div className="ml-2 pt-2">
            <SwitchInput
              label="Mais de 1 vendedor?"
              checked={hasMultipleSellers}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => toggleHasMultipleSellers(e)}
            />
          </div>
          {!hasMultipleSellers
            ? (
              <div>
                <Controller
                  name="seller_id"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <>
                      <label
                        className="block text-sm/6 font-medium text-gray-900"
                        htmlFor="seller_id"
                      >
                        Vendedor
                      </label>
                      <Select
                        id="seller_id"
                        options={sellerSelect}
                        onChange={state => onChange(state?.value)}
                        value={sellerSelect.filter(seller => seller.value === value)}
                        placeholder="Vendedor..."
                        isDisabled={!canCreate || !canUpdate}
                      />
                      {
                      errors.state && (
                        <span className="text-xs font-medium text-red-500">
                          {errors.state.message}
                        </span>
                      )
                    }
                    </>
                  )}
                />
              </div>
              )
            : (
              <>
                <div>
                  <Controller
                    name="sellers"
                    control={control}
                    render={() => (
                      <>
                        <label
                          className="block text-sm/6 font-medium text-gray-900"
                          htmlFor="sellers"
                        >
                          Vendedores
                        </label>
                        <Select
                          id="sellers"
                          options={sellerSelect}
                          onChange={state => handleAddSeller({ id: state!.value, name: state!.label })}
                          placeholder="Selecione os Vendedores"
                          isDisabled={!canCreate || !canUpdate}
                        />
                        {
                          errors.sellers && (
                            <span className="text-xs font-medium text-red-500">
                              {errors.sellers.message}
                            </span>
                          )
                        }
                      </>
                    )}
                  />
                </div>
                <div className="">
                  {selectedMultipleSellers && selectedMultipleSellers.length > 0 &&
                    <>
                      <strong>Lista de Vendedores</strong>
                      {selectedMultipleSellers.map(seller => (
                        <div key={seller.id} className="flex items-center justify-between mb-2 rounded-sm hover:bg-slate-200 px-2">
                          <span>{seller.name}</span>
                          <Button variant="ghost" size="icon" onClick={() => handleRemoveSeller(seller.id)}>
                            <FaX />
                          </Button>
                        </div>
                      ))}
                    </>}
                </div>
              </>
              )}
          {editStorePromotersCompany && (
            <div>
              <Controller
                control={control}
                name="promoters_company_id"
                render={({ field: { onChange, value } }) => (
                  <>
                    <label
                      className="block text-sm/6 font-medium text-gray-900 mb-1"
                      htmlFor="promoters_company_id"
                    >
                      Empresa de promotores
                      <span className="text-red-500 ml-2">
                        *
                      </span>
                    </label>
                    <Select
                      id="promoters_company_id"
                      options={promotersCompanySelect}
                      onChange={state => onChange(state?.value)}
                      value={promotersCompanySelect && promotersCompanySelect.filter(role => role.value === value)}
                      isDisabled={!editStorePromotersCompany}
                      placeholder="Selecione..."
                    />
                  </>
                )}
              />
            </div>
          )}
          <div>
            <Controller
              name="promoters"
              control={control}
              render={() => (
                <>
                  <label
                    className="block text-sm/6 font-medium text-gray-900"
                    htmlFor="promoters"
                  >
                    Promotores
                  </label>
                  <Select
                    id="promoters"
                    options={promoterSelect}
                    onChange={state => handleAddPromoter({ id: state!.value, name: state!.label })}
                    placeholder="Selecione os Promotores"
                    isDisabled={!updateStorePromoters || !selectedPromotersCompany}
                  />
                  {
                    errors.promoters && (
                      <span className="text-xs font-medium text-red-500">
                        {errors.promoters.message}
                      </span>
                    )
                  }
                </>
              )}
            />
          </div>
          <div className="">
            {selectedPromoters && selectedPromoters.length > 0 &&
              <>
                <strong>Lista de promotores</strong>
                {selectedPromoters.map(promoter => (
                  <div key={promoter.id} className="flex items-center justify-between mb-2 rounded-sm hover:bg-slate-200 px-2">
                    <span>{promoter.name}</span>
                    <Button variant="ghost" size="icon" onClick={() => handleRemovePromoter(promoter.id)}>
                      <FaX />
                    </Button>
                  </div>
                ))}
              </>}
          </div>
          <footer className="ml-auto pt-4">
            <Button
              type="submit"
              size="sm"
              isLoading={isLoadingForm}
            >
              Salvar
            </Button>
          </footer>
        </form>
      </div>
    </div>
  )
}
